import { useMemo } from "react";
import useSWR from "swr";
import {
  HUBSPOT_CITY_FALLBACK,
  type HubspotCityOption,
} from "@/constants/hubspot-ticket-cities";
import { visionService } from "@/services/vision-service";

export const TICKET_CITIES_SWR_KEY = "vision/ticket-cities";

/**
 * The HubSpot ticket city options, from vision360 (PED-14049). The list only
 * changes on a deploy, so it is fetched once per page and never revalidated
 * on focus; a failed fetch leaves `cities` empty and exposes `retry`.
 */
export function useTicketCities() {
  const { data, error, isLoading, mutate } = useSWR(
    TICKET_CITIES_SWR_KEY,
    () => visionService.getTicketCities(),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    },
  );

  const cities = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
    [data],
  );

  return {
    cities,
    isLoading,
    hasError: Boolean(error),
    retry: () => void mutate(),
  };
}

/** What the Vision ticket form offers: every option except the "Outras"
 * catch-all, so a ticket always names a real city. */
export function ticketFormCityOptions(
  cities: HubspotCityOption[],
): HubspotCityOption[] {
  return cities.filter((option) => option.value !== HUBSPOT_CITY_FALLBACK);
}
